import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, setToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setToken(data.token);
      await refreshUser();
      toast.success("Password updated — you're logged in.");
      navigate(data.user.paid ? "/organizer" : "/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen vector-grid flex items-center justify-center px-5 pt-24 pb-16" data-testid="reset-password-page">
      <div className="glass rounded-2xl w-full max-w-md p-8">
        <div className="w-11 h-11 rounded-xl bg-[#D4A0B4]/15 border border-[#D4A0B4]/40 flex items-center justify-center mb-5">
          <KeyRound size={18} className="text-[#D4A0B4]" />
        </div>
        <span className="eyebrow">Account</span>
        <h1 className="f-display text-3xl tracking-tight mt-2 mb-2">Choose a new password</h1>
        {!token ? (
          <p className="text-sm text-[#8C7562] leading-relaxed" data-testid="reset-missing-token">
            This link is missing its reset code. Open the link from your email again, or <Link to="/" className="text-[#A8785A] underline">request a new one</Link>.
          </p>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4 mt-4">
            <input className="field" type="password" placeholder="New password (6+ characters)" value={password} autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
            <input className="field" type="password" placeholder="Confirm new password" value={confirm} autoComplete="new-password"
              onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
            {error && <p className="text-xs text-[#A8785A] font-medium" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy || password.length < 6} className="btn-primary justify-center disabled:opacity-60" data-testid="reset-submit-btn">
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}
